import React, { useState, useEffect, useCallback } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Grid,
  MenuItem,
  TextField,
  Autocomplete,
  Box,
} from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import _ from "lodash";
import { useFormik } from "formik";
import * as yup from "yup";
import dayjs from "dayjs";

//Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

//
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

//Services
import LocationApi from "Services/Location";

const RestrictedLocationModalUpdate = (props) => {
  const { open, handleClose, locationId } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [values, setValues] = useState(null);
  const [locationData, setLocationData] = useState([]);

  //Time pickers
  const [startingTime, setStartingTime] = useState(null);
  const [endingTime, setEndingTime] = useState(null);

  const [startingTimeData, setStartingTimeData] = useState([]);
  const [endingTimeData, setEndingTimeData] = useState([]);

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [restrictLocationDetails, setRestrictLocationDetails] = useState([]);

  const getAllLocations = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setValues(res?.data?.data?.data);
      } else {
        setValues(null);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    LocationApi.getServiceArea().then(onSuccess, onFailure);
  }, [dispatch]);

  const getRestrictLocationDetails = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setRestrictLocationDetails(res?.data.data);
      } else {
        setRestrictLocationDetails(null);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    LocationApi.restrictLocationDetailsGet(locationId).then(onSuccess, onFailure);
  }, [locationId, dispatch]);

  useEffect(() => {
    if (open) {
      getRestrictLocationDetails();
      getAllLocations();
    }
  }, [open, getRestrictLocationDetails, getAllLocations]);

  useEffect(() => {
    if (restrictLocationDetails && Object.keys(restrictLocationDetails).length > 0) {
      setSelectedLocation({
        locationId: restrictLocationDetails?.locationId?._id,
        locationName: restrictLocationDetails?.locationId?.locationName,
      });

      formik.setFieldValue("restrictType", restrictLocationDetails?.restrictionType);

      const startingTimeFormatted = dayjs()
        .set("hour", restrictLocationDetails?.startingTime?.hour)
        .set("minute", restrictLocationDetails?.startingTime?.minutes)
        .set("second", restrictLocationDetails?.startingTime?.seconds);

      const endingTimeFormatted = dayjs()
        .set("hour", restrictLocationDetails?.endingTime?.hour)
        .set("minute", restrictLocationDetails?.endingTime?.minutes)
        .set("second", restrictLocationDetails?.endingTime?.seconds);

      setStartingTime(startingTimeFormatted);
      setEndingTime(endingTimeFormatted);

      setStartingTimeData({
        hour: restrictLocationDetails?.startingTime?.hour,
        minutes: restrictLocationDetails?.startingTime?.minutes,
        seconds: restrictLocationDetails?.startingTime?.seconds,
        timeFormat: restrictLocationDetails?.startingTime?.timeFormat,
      });
      setEndingTimeData({
        hour: restrictLocationDetails?.endingTime?.hour,
        minutes: restrictLocationDetails?.endingTime?.minutes,
        seconds: restrictLocationDetails?.endingTime?.seconds,
        timeFormat: restrictLocationDetails?.endingTime?.timeFormat,
      });

      formik.setFieldValue("startingTime", startingTimeFormatted.format("YYYY-MM-DDTHH:mm:ss"));
      formik.setFieldValue("endingTime", endingTimeFormatted.format("YYYY-MM-DDTHH:mm:ss"));
    }
  }, [restrictLocationDetails]);

  const UpdateRestrictLocation = (formValues) => {
    dispatch(showLoader("Loading please wait..."));

    let data = {
      locationId: selectedLocation?.locationId || selectedLocation?._id,
      restrictionType: formValues?.restrictType,
      startingTime: {
        hour: startingTimeData?.hour,
        minutes: startingTimeData?.minutes,
        seconds: startingTimeData?.seconds,
        timeFormat: startingTimeData?.timeFormat,
      },
      endingTime: {
        hour: endingTimeData?.hour,
        minutes: endingTimeData?.minutes,
        seconds: endingTimeData?.seconds,
        timeFormat: endingTimeData?.timeFormat,
      },
    };

    const onSuccess = (res) => {
      setStartingTime(null);
      setEndingTime(null);
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: res?.data?.message || "Updated successfully",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "success",
        })
      );
      handleClose();
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while updating",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    LocationApi.restrictLocationUpdate(locationId, data).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      locationName: null,
      restrictType: "PICKUP",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      locationName: yup
        .string()
        .nullable()
        .trim()
        .min(3, "Name must be at least 3 letters")
        .max(50, "Max 50 characters are allowed"),
    }),
    onSubmit: UpdateRestrictLocation,
  });

  useEffect(() => {
    if (!_.isEmpty(values)) {
      setLocationData(values);
    }
  }, [values]);

  const handleAutocompleteChange = (event, newValue) => {
    setSelectedLocation(newValue);
  };

  const handleStartingTimeChange = (newValue) => {
    setStartingTime(newValue);
    if (newValue) {
      const selectedDate = newValue instanceof Date ? newValue : new Date(newValue);
      const hour = selectedDate.getHours();
      const minutes = selectedDate.getMinutes();
      const seconds = selectedDate.getSeconds();
      const timeFormat = hour >= 12 ? 12 : 24;

      setStartingTimeData({
        hour,
        minutes,
        seconds,
        timeFormat,
      });
    }
  };

  const handleEndingTimeChange = (newValue) => {
    formik.setFieldError("endingTime", "");
    setEndingTime(newValue);
    if (newValue < startingTime) {
      formik.setFieldError("endingTime", "Ending time cannot be less than starting time");
    } else {
      if (newValue) {
        const selectedDate = newValue instanceof Date ? newValue : new Date(newValue);
        const hour = selectedDate.getHours();
        const minutes = selectedDate.getMinutes();
        const seconds = selectedDate.getSeconds();
        const timeFormat = hour >= 12 ? 12 : 24;

        setEndingTimeData({ hour, minutes, seconds, timeFormat });
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        formik.resetForm();
        setStartingTime(null);
        setEndingTime(null);
        handleClose();
      }}
      classes={{ paper: classes.dialogPaper }}
    >
      <Box className={classes.headerContainer}>
        <Typography className={classes.dialogTitle}>Update Restricted Location</Typography>
        <Typography className={classes.breadcrumb}>
          Admin / <span>Restricted Location</span>
        </Typography>
      </Box>

      <DialogContent style={{ padding: 0 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Location Name (Autocomplete) */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Location Name</Typography>
              <Autocomplete
                disablePortal
                name="locationName"
                id="locationName"
                value={selectedLocation}
                onChange={handleAutocompleteChange}
                getOptionLabel={(location) => location?.locationName || ""}
                options={locationData}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select a Location"
                    className={classes.formInput}
                    error={formik.touched.locationName && Boolean(formik.errors.locationName)}
                    helperText={formik.touched.locationName && formik.errors.locationName}
                  />
                )}
              />
            </Grid>

            {/* Restrict Type */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Restrict Type</Typography>
              <TextField
                name="restrictType"
                select
                value={formik.values.restrictType}
                onChange={formik.handleChange}
                error={formik.touched.restrictType && Boolean(formik.errors.restrictType)}
                helperText={formik.touched.restrictType && formik.errors.restrictType}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
              >
                {["PICKUP", "DROP", "ALL"].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === "PICKUP" ? "Pickup" : option === "DROP" ? "Drop" : "All"}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Starting Time */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Starting Time</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  value={startingTime}
                  onChange={handleStartingTimeChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      className={classes.formInput}
                      error={formik.touched.startingTime && Boolean(formik.errors.startingTime)}
                      helperText={formik.touched.startingTime && formik.errors.startingTime}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            {/* Ending Time */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Ending Time</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  value={endingTime}
                  onChange={handleEndingTimeChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      className={classes.formInput}
                      error={Boolean(formik.errors.endingTime)}
                      helperText={formik.errors.endingTime}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          <Box className={classes.dialogActions}>
            <Button
              className={classes.cancelButton}
              onClick={() => {
                formik.resetForm();
                setStartingTime(null);
                setEndingTime(null);
                handleClose();
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              className={classes.submitButton}
            >
              <EditOutlined style={{ fontSize: "18px" }} />
              Update Restriction
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RestrictedLocationModalUpdate;
